// FLEET LIVE's stream half (#6778, epic #6552): what the `fleet_live` topic's rows look
// like, and how they fold onto the `GET /v1/fleet/live` body.
//
// THE FRAME SPLIT is the server's (`fleetLiveFrames`, `apps/api/src/routes/fleet-live.ts`):
// one `item:<id>` row per item, and four synthetic rows. Each schema below is DERIVED from
// `FleetLiveWireSchema`, never re-typed, so a field the wire gains or loses moves here too:
//   - `fleet_live:summary` is the body minus `items`, `machines`, `squads`, `squads_more`
//     and `events`, AS THE BODY HAS IT (an omitted field is omitted from the frame);
//   - `fleet_live:machines` is the bare `machines` object;
//   - `fleet_live:squads` is `{ squads, squads_more }` (#6779 reads it; validated here so a
//     snapshot does not warn);
//   - `fleet_live:events` is `{ events }`.
//
// This topic carries five shapes, so no generic "rows of one `T`" read can fold it: the merge
// goes BY KEY, and any other key is ignored.

import { type FleetLiveWire, FleetLiveWireSchema } from '@developerz/domain';
import type { z } from 'zod';

export const FLEET_LIVE_TOPIC = 'fleet_live';

/** The synthetic rows' keys, as `fleetLiveFrames` emits them. */
export const FLEET_LIVE_FRAME_KEYS = {
  summary: 'fleet_live:summary',
  machines: 'fleet_live:machines',
  squads: 'fleet_live:squads',
  events: 'fleet_live:events',
} as const;

/** An item row's key prefix (`item:<id>`). */
export const FLEET_LIVE_ITEM_PREFIX = 'item:';

type FleetLiveItemWire = FleetLiveWire['items'][number];

const ItemFrameSchema = FleetLiveWireSchema.shape.items.element;
const SummaryFrameSchema = FleetLiveWireSchema.omit({
  items: true,
  machines: true,
  squads: true,
  squads_more: true,
  events: true,
});
const MachinesFrameSchema = FleetLiveWireSchema.shape.machines;
const SquadsFrameSchema = FleetLiveWireSchema.pick({ squads: true, squads_more: true });
const EventsFrameSchema = FleetLiveWireSchema.pick({ events: true });

type SummaryFrame = z.infer<typeof SummaryFrameSchema>;

const FRAME_SCHEMAS: ReadonlyMap<string, z.ZodType> = new Map<string, z.ZodType>([
  [FLEET_LIVE_FRAME_KEYS.summary, SummaryFrameSchema],
  [FLEET_LIVE_FRAME_KEYS.machines, MachinesFrameSchema],
  [FLEET_LIVE_FRAME_KEYS.squads, SquadsFrameSchema],
  [FLEET_LIVE_FRAME_KEYS.events, EventsFrameSchema],
]);

/**
 * The row shape one `fleet_live` key carries, or `undefined` for a key this app does not
 * know (the stream door drops it rather than storing an unchecked row). A `Map`, for
 * `account-stream-frames.ts`' reason: the key arrives off the wire.
 */
export function fleetLiveRowSchema(key: string): z.ZodType | undefined {
  if (key.startsWith(FLEET_LIVE_ITEM_PREFIX)) return ItemFrameSchema;
  return FRAME_SCHEMAS.get(key);
}

/**
 * The body this section draws: the REST read, with the topic folded on once it is served.
 *
 * - topic absent (`rows === undefined`): the REST body (the stream is not the truth);
 * - topic served but its summary frame not received yet (a fresh `ready` before the
 *   snapshot lands): the REST body still, so a reconnect does not blank the deck;
 * - topic served: its `item:<id>` rows ARE the item set (an item the stream removed is
 *   gone), and each synthetic row REPLACES its fields whole (a summary field the frame
 *   omits is omitted, never kept from first paint); a synthetic row other than the summary
 *   not received yet falls back to the REST body's fields.
 *
 * Rows reach this function already validated by the stream door (`acceptPatch`), so the
 * casts below name a shape that was parsed, not hoped for. `undefined` until there is a
 * body to draw.
 */
export function mergeFleetLive(
  rest: FleetLiveWire | undefined,
  rows: Readonly<Record<string, unknown>> | undefined,
): FleetLiveWire | undefined {
  if (rows === undefined) return rest;
  const summary = rows[FLEET_LIVE_FRAME_KEYS.summary] as SummaryFrame | undefined;
  if (summary === undefined) return rest;
  const machines = rows[FLEET_LIVE_FRAME_KEYS.machines] as FleetLiveWire['machines'] | undefined;
  const squads = rows[FLEET_LIVE_FRAME_KEYS.squads] as
    | Pick<FleetLiveWire, 'squads' | 'squads_more'>
    | undefined;
  const events = rows[FLEET_LIVE_FRAME_KEYS.events] as Pick<FleetLiveWire, 'events'> | undefined;
  const items = Object.entries(rows)
    .filter(([key]) => key.startsWith(FLEET_LIVE_ITEM_PREFIX))
    .map(([, row]) => row as FleetLiveItemWire);
  return {
    ...summary,
    items,
    machines: machines ?? rest?.machines ?? { shown: [] },
    squads: squads?.squads ?? rest?.squads ?? [],
    squads_more: squads?.squads_more ?? rest?.squads_more ?? 0,
    events: events?.events ?? rest?.events ?? [],
  };
}
